import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import PageTitle from '../components/PageTitle';

const API_BASE = process.env.REACT_APP_API_URL || '';

const STATUS_STYLE = {
  SUBMITTED:    { label: 'Submitted',    icon: '📨', cls: 'badge-submitted' },
  UNDER_REVIEW: { label: 'Under Review', icon: '🔍', cls: 'badge-under_review' },
  FILED:        { label: 'Filed',        icon: '✅', cls: 'badge-filed' },
};

function ReturnBadge({ status }) {
  const s = STATUS_STYLE[status] || { label: status, icon: '•', cls: '' };
  return <span className={`badge ${s.cls}`}>{s.icon} {s.label}</span>;
}

// ── Admin GST Returns List ──────────────────────────────────────
export function AdminGstReturnList() {
  const [returns, setReturns]   = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [filter, setFilter]     = useState('ALL');
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    api.get('/api/gst-return/all').then(r => { setReturns(r.data); setFiltered(r.data); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let list = returns;
    if (filter !== 'ALL') list = list.filter(r => r.status === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(r => r.gstin?.toLowerCase().includes(q) ||
        r.businessName?.toLowerCase().includes(q) || r.returnType?.toLowerCase().includes(q));
    }
    setFiltered(list);
  }, [filter, search, returns]);

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;

  return (
    <>
      <PageTitle title="GST Returns" />
      <div className="page-hero">
        <div className="page-hero-label">Admin Panel</div>
        <h1>GST Return Cases</h1>
        <p>{returns.length} total · {returns.filter(r => r.status === 'SUBMITTED').length} pending</p>
      </div>
      <div className="page-wide">

        {/* Stats */}
        <div className="stat-grid" style={{ marginBottom: '1.5rem' }}>
          {[
            { label: 'Total',        value: returns.length,                                       icon: '📋' },
            { label: 'Submitted',    value: returns.filter(r => r.status === 'SUBMITTED').length, icon: '📨', highlight: true },
            { label: 'Under Review', value: returns.filter(r => r.status === 'UNDER_REVIEW').length, icon: '🔍' },
            { label: 'Filed',        value: returns.filter(r => r.status === 'FILED').length,     icon: '✅' },
          ].map(s => (
            <div key={s.label} className="stat-card"
              style={s.highlight && s.value > 0 ? { border: '2px solid var(--gold)' } : {}}>
              <div style={{ fontSize: '1.3rem', marginBottom: '.2rem' }}>{s.icon}</div>
              <div className="stat-num">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="Search GSTIN, business name, return type..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 220, padding: '.6rem 1rem', border: '1.5px solid var(--border)', borderRadius: 10, fontFamily: 'DM Sans, sans-serif', fontSize: '.88rem', background: '#fff', outline: 'none' }} />
          <select value={filter} onChange={e => setFilter(e.target.value)}
            style={{ padding: '.6rem 1rem', border: '1.5px solid var(--border)', borderRadius: 10, fontFamily: 'DM Sans, sans-serif', fontSize: '.88rem', background: '#fff' }}>
            <option value="ALL">All Statuses</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="UNDER_REVIEW">Under Review</option>
            <option value="FILED">Filed</option>
          </select>
          <span style={{ fontSize: '.82rem', color: 'var(--muted)' }}>{filtered.length} results</span>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr><th>#</th><th>Business</th><th>GSTIN</th><th>Type</th><th>Period</th><th>Turnover</th><th>Tax Payable</th><th>Status</th><th>Date</th><th>Action</th></tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={10} style={{ textAlign: 'center', color: 'var(--muted)', padding: '2rem' }}>No returns found</td></tr>
                )}
                {filtered.map(r => (
                  <tr key={r.id}>
                    <td style={{ color: 'var(--muted)' }}>#{r.id}</td>
                    <td><strong style={{ fontSize: '.88rem' }}>{r.businessName}</strong><br /><span style={{ fontSize: '.75rem', color: 'var(--muted)' }}>{r.email}</span></td>
                    <td style={{ fontFamily: 'monospace', fontSize: '.8rem' }}>{r.gstin}</td>
                    <td><span style={{ background: 'var(--bluel)', color: 'var(--blue)', fontSize: '.72rem', fontWeight: 700, padding: '.15rem .5rem', borderRadius: 4 }}>{r.returnType}</span></td>
                    <td style={{ fontSize: '.83rem' }}>{r.filingPeriod}</td>
                    <td style={{ fontSize: '.83rem' }}>₹{Number(r.totalTurnover || 0).toLocaleString('en-IN')}</td>
                    <td style={{ fontSize: '.83rem', fontWeight: 600 }}>₹{Number(r.taxPayable || 0).toLocaleString('en-IN')}</td>
                    <td><ReturnBadge status={r.status} /></td>
                    <td style={{ fontSize: '.78rem', color: 'var(--muted)' }}>{new Date(r.submittedAt).toLocaleDateString('en-IN')}</td>
                    <td><Link to={`/admin/gst-returns/${r.id}`}><button className="btn btn-primary btn-sm">Review</button></Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Admin GST Return Detail ─────────────────────────────────────
export function AdminGstReturnDetail() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const [ret, setRet]         = useState(null);
  const [status, setStatus]   = useState('');
  const [note, setNote]       = useState('');
  const [arn, setArn]         = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState(null);

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  useEffect(() => {
    api.get(`/api/gst-return/${id}`).then(r => {
      setRet(r.data); setStatus(r.data.status); setNote(r.data.adminNote || ''); setArn(r.data.arn || '');
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  const saveStatus = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams({ status });
      if (note) params.append('note', note);
      if (arn)  params.append('arn', arn);
      const { data } = await api.patch(`/api/gst-return/${id}/status?${params}`);
      setRet(data);
      showMsg('success', `Status updated to ${status.replace(/_/g, ' ')}`);
    } catch (err) { showMsg('error', 'Update failed'); }
    finally { setSaving(false); }
  };

  const uploadFiled = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    const { data } = await api.post(`/api/gst-return/${id}/upload-filed`, fd);
    setRet(data); setStatus(data.status);
    showMsg('success', 'Filed return uploaded — status set to Filed!');
  };

  const downloadFile = async (filename, label) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/files/download/${filename}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = label + '_' + (filename.includes('_') ? filename.substring(filename.indexOf('_') + 1) : filename);
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { alert('Download failed'); }
  };

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;
  if (!ret)    return <div className="centered"><p>Return not found.</p></div>;

  return (
    <>
      <PageTitle title={`GST Return #${ret.id}`} />
      <div className="page-hero">
        <div className="page-hero-label">Admin — GST Return Review</div>
        <h1>{ret.returnType} — {ret.filingPeriod}</h1>
        <p>{ret.businessName} · {ret.gstin}</p>
      </div>
      <div className="page-wide">
        <button className="btn btn-outline btn-sm" style={{ marginBottom: '1.5rem' }} onClick={() => navigate('/admin/gst-returns')}>← Back</button>

        {msg && <div className={`alert alert-${msg.type}`} style={{ marginBottom: '1.5rem' }}>{msg.text}</div>}

        <div className="detail-grid">

          {/* Left */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

            {/* Business & Return Info */}
            <div className="card">
              <SectionTitle>Return Information</SectionTitle>
              <div className="two-col" style={{ gap: '.5rem' }}>
                <InfoRow label="GSTIN"         value={ret.gstin} />
                <InfoRow label="Business Name" value={ret.businessName} />
                <InfoRow label="Return Type"   value={ret.returnType} />
                <InfoRow label="Filing Period" value={ret.filingPeriod} />
                <InfoRow label="Email"         value={ret.email} />
                <InfoRow label="Phone"         value={ret.phone} />
                {ret.arn && <InfoRow label="ARN" value={ret.arn} />}
              </div>
            </div>

            {/* Sales Summary */}
            <div className="card">
              <SectionTitle>Sales & Output Tax</SectionTitle>
              <div className="two-col" style={{ gap: '.5rem' }}>
                <InfoRow label="Total Turnover"      value={ret.totalTurnover ? `₹${Number(ret.totalTurnover).toLocaleString('en-IN')}` : null} />
                <InfoRow label="Taxable Supplies"    value={ret.taxableSupplies ? `₹${Number(ret.taxableSupplies).toLocaleString('en-IN')}` : null} />
                <InfoRow label="Exempt Supplies"     value={ret.exemptSupplies ? `₹${Number(ret.exemptSupplies).toLocaleString('en-IN')}` : null} />
                <InfoRow label="Output GST Collected" value={ret.totalTaxCollected ? `₹${Number(ret.totalTaxCollected).toLocaleString('en-IN')}` : null} />
              </div>
            </div>

            {/* ITC Summary */}
            <div className="card">
              <SectionTitle>Purchases & ITC</SectionTitle>
              <div className="two-col" style={{ gap: '.5rem' }}>
                <InfoRow label="Total Purchases"  value={ret.totalPurchases ? `₹${Number(ret.totalPurchases).toLocaleString('en-IN')}` : null} />
                <InfoRow label="ITC Available"    value={ret.itcAvailable ? `₹${Number(ret.itcAvailable).toLocaleString('en-IN')}` : null} />
                <InfoRow label="ITC Utilized"     value={ret.itcUtilized ? `₹${Number(ret.itcUtilized).toLocaleString('en-IN')}` : null} />
                <InfoRow label="Net Tax Payable"  value={ret.taxPayable ? `₹${Number(ret.taxPayable).toLocaleString('en-IN')}` : null} />
                <InfoRow label="Tax Paid"         value={ret.taxPaid ? `₹${Number(ret.taxPaid).toLocaleString('en-IN')}` : null} />
              </div>
              {ret.taxPayable && ret.taxPaid && (
                <div className={`alert ${Number(ret.taxPaid) >= Number(ret.taxPayable) ? 'alert-success' : 'alert-error'}`} style={{ marginTop: '1rem', fontSize: '.83rem' }}>
                  {Number(ret.taxPaid) >= Number(ret.taxPayable)
                    ? '✅ Tax fully paid'
                    : `⚠️ Balance payable: ₹${(Number(ret.taxPayable) - Number(ret.taxPaid)).toLocaleString('en-IN')}`}
                </div>
              )}
            </div>

            {/* Documents */}
            <div className="card">
              <SectionTitle>Documents (Download)</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
                {/* Single-file documents */}
                {[['Sales Register', ret.docSalesRegister], ['Purchase Register', ret.docPurchaseRegister], ['Tax Challan', ret.docChallan]
                ].map(([label, filename]) => filename ? (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.6rem .9rem', background: 'var(--greenl)', border: '1px solid #a8d5b9', borderRadius: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                      <span>📄</span>
                      <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--green)' }}>{label}</span>
                    </div>
                    <button onClick={() => downloadFile(filename, label)}
                      style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 7, padding: '.32rem .8rem', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer' }}>
                      ⬇ Download
                    </button>
                  </div>
                ) : (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.6rem .9rem', background: 'var(--creamd)', borderRadius: 8, opacity: .5 }}>
                    <span style={{ fontSize: '.82rem', color: 'var(--muted)' }}>— {label}</span>
                    <span style={{ fontSize: '.72rem', color: 'var(--muted)' }}>Not uploaded</span>
                  </div>
                ))}

                {/* Multi-file: Invoices */}
                <MultiFileDownload label="Invoices" jsonFilenames={ret.docInvoices} onDownload={downloadFile} />

                {/* Multi-file: Other Documents */}
                <MultiFileDownload label="Other Documents" jsonFilenames={ret.docOther} onDownload={downloadFile} />
              </div>
              {ret.remarks && <div className="alert alert-info" style={{ marginTop: '1rem', fontSize: '.82rem' }}>💬 {ret.remarks}</div>}
            </div>

            {/* Upload filed return */}
            <div className="card">
              <SectionTitle>Upload Filed Return Acknowledgement</SectionTitle>
              <p style={{ fontSize: '.82rem', color: 'var(--muted)', marginBottom: '1rem' }}>
                After filing on the GST portal, upload the acknowledgement — status auto-sets to Filed and user can download it.
              </p>
              {ret.docFiledReturn ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.75rem 1rem', background: 'var(--greenl)', border: '1px solid #a8d5b9', borderRadius: 10 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '.85rem', color: 'var(--green)' }}>✅ Filed return uploaded</div>
                    <div style={{ fontSize: '.72rem', color: 'var(--muted)' }}>User can download from their dashboard</div>
                  </div>
                  <button onClick={() => downloadFile(ret.docFiledReturn, 'Filed_GST_Return')}
                    style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 7, padding: '.32rem .8rem', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer' }}>
                    ⬇ Download
                  </button>
                </div>
              ) : (
                <input type="file" accept=".pdf,.jpg,.png" style={{ fontSize: '.82rem' }}
                  onChange={e => { if (e.target.files[0]) uploadFiled(e.target.files[0]); }} />
              )}
            </div>
          </div>

          {/* Right: Status management */}
          <div>
            <div className="card" style={{ position: 'sticky', top: '5rem' }}>
              <SectionTitle>Update Status</SectionTitle>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '.75rem', color: 'var(--muted)', marginBottom: '.35rem' }}>Current</div>
                <ReturnBadge status={ret.status} />
              </div>

              <div className="field" style={{ marginBottom: '1rem' }}>
                <label>Set Status</label>
                <select value={status} onChange={e => setStatus(e.target.value)}>
                  <option value="SUBMITTED">Submitted</option>
                  <option value="UNDER_REVIEW">Under Review</option>
                  <option value="FILED">Filed</option>
                </select>
              </div>

              <div className="field" style={{ marginBottom: '1rem' }}>
                <label>ARN (Acknowledgement No.)</label>
                <input value={arn} onChange={e => setArn(e.target.value.toUpperCase())}
                  placeholder="AA270921000000X" style={{ fontFamily: 'monospace', letterSpacing: '.04em' }} />
                <span className="hint">Enter after filing on GST portal</span>
              </div>

              <div className="field" style={{ marginBottom: '1.25rem' }}>
                <label>Note to User</label>
                <textarea rows={3} value={note} onChange={e => setNote(e.target.value)}
                  placeholder="Shown on user's dashboard..." />
              </div>

              <button className="btn btn-gold" style={{ width: '100%', justifyContent: 'center' }}
                onClick={saveStatus} disabled={saving}>
                {saving ? <><span className="spinner" /> Saving...</> : '💾 Update Status'}
              </button>

              {/* Workflow */}
              <div style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid var(--creamd)' }}>
                <SectionTitle>Workflow</SectionTitle>
                {[['1','SUBMITTED','⬇ Download & verify documents'],['2','UNDER_REVIEW','🖥 File on GST portal'],['3','FILED','⬆ Upload acknowledgement + ARN']].map(([n,s,desc]) => (
                  <div key={s} style={{ display: 'flex', gap: '.6rem', marginBottom: '.5rem' }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: ret.status === s ? 'var(--green)' : 'var(--creamd)', color: ret.status === s ? '#fff' : 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.65rem', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{n}</div>
                    <div>
                      <div style={{ fontSize: '.75rem', fontWeight: 600, color: ret.status === s ? 'var(--green)' : 'var(--text)' }}>{s.replace(/_/g,' ')}</div>
                      <div style={{ fontSize: '.7rem', color: 'var(--muted)' }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--creamd)', fontSize: '.73rem', color: 'var(--muted)' }}>
                Submitted: {new Date(ret.submittedAt).toLocaleString('en-IN')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Multi-file download component ─────────────────────────────────
function MultiFileDownload({ label, jsonFilenames, onDownload }) {
  let files = [];
  if (jsonFilenames) {
    try { files = JSON.parse(jsonFilenames); } catch { files = [jsonFilenames]; }
  }

  if (files.length === 0) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.6rem .9rem', background: 'var(--creamd)', borderRadius: 8, opacity: .5 }}>
      <span style={{ fontSize: '.82rem', color: 'var(--muted)' }}>— {label}</span>
      <span style={{ fontSize: '.72rem', color: 'var(--muted)' }}>Not uploaded</span>
    </div>
  );

  return (
    <div style={{ border: '1px solid #a8d5b9', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ background: 'var(--greenl)', padding: '.5rem .9rem', display: 'flex', alignItems: 'center', gap: '.5rem' }}>
        <span>📁</span>
        <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--green)' }}>{label}</span>
        <span style={{ background: 'var(--green)', color: '#fff', fontSize: '.68rem', fontWeight: 700, padding: '.1rem .4rem', borderRadius: 10 }}>{files.length} file{files.length > 1 ? 's' : ''}</span>
      </div>
      {files.map((filename, i) => {
        const displayName = filename.includes('_') ? filename.substring(filename.indexOf('_') + 1) : filename;
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.5rem .9rem', borderTop: '1px solid #a8d5b9', background: '#fff' }}>
            <span style={{ fontSize: '.8rem', color: 'var(--navy)' }}>📄 {displayName}</span>
            <button onClick={() => onDownload(filename, `${label}_${i + 1}`)}
              style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 6, padding: '.25rem .65rem', fontSize: '.75rem', fontWeight: 600, cursor: 'pointer' }}>
              ⬇
            </button>
          </div>
        );
      })}
    </div>
  );
}


function SectionTitle({ children }) {
  return <div style={{ fontWeight: 600, fontSize: '.82rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--navy)', marginBottom: '1rem' }}>{children}</div>;
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ padding: '.4rem 0', borderBottom: '1px solid var(--creamd)', fontSize: '.83rem' }}>
      <div style={{ color: 'var(--muted)', fontSize: '.72rem', marginBottom: '.1rem' }}>{label}</div>
      <div style={{ fontWeight: 500, color: 'var(--navy)' }}>{value}</div>
    </div>
  );
}